/**
 * DST amount field helpers — disable Appkit comma display via [format]="''" in HTML,
 * then strip any commas on load/save so API receives raw numeric strings.
 */

export function stripAmountFormatting(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return value.toString().replace(/,/g, '');
}

/** Strip commas from each element of an amount array (e.g. BasisAmt, FMVAmt). */
export function stripAmountArray(values: (string | number | null | undefined)[] | null | undefined): string[] {
  if (!values || !values.length) {
    return [];
  }
  return values.map(v => stripAmountFormatting(v));
}

/** Apply stripAmountFormatting to selected keys on a save payload object (mutates in place). */
export function stripAmountFields(obj: Record<string, any>, keys: string[]): void {
  keys.forEach(key => {
    const val = obj[key];
    if (val === null || val === undefined) {
      return;
    }
    if (Array.isArray(val)) {
      obj[key] = stripAmountArray(val);
    } else {
      obj[key] = stripAmountFormatting(val);
    }
  });
}

/** Strip commas from component properties after loading from attMap. */
export function stripComponentAmounts(component: Record<string, any>, propertyNames: string[]): void {
  propertyNames.forEach(prop => {
    const val = component[prop];
    if (val !== null && val !== undefined && val !== '') {
      component[prop] = stripAmountFormatting(val);
    }
  });
}

export const SEC8883_AMOUNT_KEYS = [
  'StockPrice', 'AcquisitionCostSC', 'TargetLiabilities', 'AGUBorADSP',
  'Class1AFMV', 'Class1AGUBOrADSP', 'Class2AFMV', 'Class2AGUBOrADSP', 'Class3AFMV', 'Class3AGUBOrADSP',
  'Class4AFMV', 'Class4AGUBOrADSP', 'Class5AFMV', 'Class5AGUBOrADSP', 'Class6N7AFMV', 'Class6N7AGUBOrADSP',
  'TotalAFMV', 'TotalAGUB',
  'Class1ASPPR', 'Class1RtnAllAGUB', 'Class2ASPPR', 'Class2RtnAllAGUB', 'Class3ASPPR', 'Class3RtnAllAGUB',
  'Class4ASPPR', 'Class4RtnAllAGUB', 'Class5ASPPR', 'Class5RtnAllAGUB', 'Class6N7ASPPR', 'Class6N7RtnAllAGUB',
  'TotalASPPR', 'TotalRtnAllAGUB',
  'Class1IncDec', 'Class2IncDec', 'Class3IncDec', 'Class4IncDec', 'Class5IncDec', 'Class6N7IncDec'
];

export const SEC8594_AMOUNT_KEYS = [
  'TotalSalesPrice',
  'Class1FMV', 'Class1ASP', 'Class2FMV', 'Class2ASP', 'Class3FMV', 'Class3ASP',
  'Class4FMV', 'Class4ASP', 'Class5FMV', 'Class5ASP', 'Class6N7FMV', 'Class6N7ASP',
  'TotalFMV', 'TotalASP',
  'Class1PPR', 'Class1RASP', 'Class2PPR', 'Class2RASP', 'Class3PPR', 'Class3RASP',
  'Class4PPR', 'Class4RASP', 'Class5PPR', 'Class5RASP', 'Class6N7PPR', 'Class6N7RASP',
  'TotalPPR', 'TotalRASP',
  'Class1IncDec', 'Class2IncDec', 'Class3IncDec', 'Class4IncDec', 'Class5IncDec', 'Class6N7IncDec'
];

export const SEC8858_AMOUNT_KEYS = [
  'f8858SchGDualCnslLossAmt', 'f8858SchGSepCombDualLossAmt', 'f8858SchGNetIncmLossAmt',
  'f8858SchGCumilativeRegAmt', 'f8858SchGTrigRecapAmt'
];

export const FORM8621_SCALAR_AMOUNT_KEYS = [
  'ValueOfSharesRangeE', 'Section1291Amt', 'Section1293Amt', 'Section1296Amt',
  'ProRataShareOfQEFOrdinaryEarn', 'IncomePortionOfOrdinaryEarn', 'DividendIncome',
  'ProRataShareOfTotalNetCapGain', 'IncomePortionOfNetCapitalGain', 'NetLongTermCapitalGain',
  'DividendIncomeAndNetLTCG', 'TotalCashAndDistributions', 'PortionOfProRataOrdnryEarnings',
  'TotalCashAndPortionOfProRata', 'ProRataLessCashAndPortion', 'TotalTaxForTheTaxYear',
  'TotTaxWithoutProRataLessCash', 'DeferredTax', 'FairMarketValueOfPFICStock',
  'AdjustedBasisInStockEndOfTY', 'Excess', 'UnreversedInclusions', 'ExcessOrUnreservedInclusions',
  'FMVStkOnDtSaleOrDisposAmt', 'AdjBasisStkOnDtSaleOrDisposAmt', 'OrdinaryIncomeFromPFICStkAmt',
  'StkSaleUnreversedInclusionsAmt', 'LossLimitedByOrdinaryIncomeAmt', 'LossExcessOfUnrvrsdInclsnAmt'
];

/** Component property names for Form 8621 edit mode. Strip commas after load so ap-field inputs display values. */
export const FORM8621_COMPONENT_AMOUNT_PROPS = [
  'noOfSharesYearEnd', 'valueOfSharesYearEnd', 'pficType1Amount', 'pficType2Amount', 'pficType3Amount',
  'proRateShares', 'portionLine6a', 'proRateSharesNetGain', 'portionLine7a',
  'cashPropertyDeemedDistributedCash', 'portionLine8c', 'totalTax', 'totalTaxWithoutRegard',
  'fairMarketValue', 'adjustedBasisStock', 'unreversedInclusion', 'lossLine10c',
  'fairMarketDispositionValue', 'adjustedBasisDispositionStock', 'unreversedInclusionSection1296',
  'lossLine13c', 'lossLine13cExceedInclusion',
  'partFiveCurrencyCode', 'totalDistribution', 'totalDistributionByFund', 'precedingTaxYear',
  'distriAndDisposOfStockTypTotalExcessDistributionUS', 'gainOrLossDistributionSection1291',
  'totalAmountLine16a', 'aggIncreaseInTax', 'ftc', 'interestTaxNetIncrease',
  'unDistEarnings', 'deferredTax', 'deferredTaxInterest', 'earnDistOrDeemed',
  'deferredTaxDue', 'accrdInterestDue', 'deferredTaxAftTerm', 'accrdInterestAftTerm'
];

export const FORM8621_ARRAY_AMOUNT_KEYS = [
  'DistriAndDispositionOfStockTotalPFICDistriDuringCurrentTY',
  'DistriAndDispositionOfStockDistributionsIn3PrecedingTY',
  'DistriAndDispositionOfStockAvgDistribution3PrecedingTY',
  'DistriAndDispositionOfStockAverageDistri3PreviousTY125Pct',
  'DistriAndDispositionOfStockTotalExcessDistribution',
  'DistriAndDisposOfStockTypTotalExcessDistributionUS',
  'DistriAndDispositionOfStockGainLossFromDispositionOfStock',
  'DistriAndDispositionOfStockTotAllocatableCurrAndPrePFICTY',
  'DistriAndDispositionOfStockAggregateIncreaseInTaxEachTY',
  'DistriAndDispositionOfStockForeignTaxCredit',
  'DistriAndDispositionOfStockAggregateIncrLessForeignTaxCr',
  'DistriAndDispositionOfStockInterestOnEachNetIncreaseInTax',
  'ElectionStatusUndistributedEarnings', 'ElectionStatusDeferredTax',
  'ElectionStatusInterestAccruedOnDeferredTax', 'ElectionStatusEarningsDistributedDuringTheTY',
  'ElectionStatusDeferredTaxDueWithThisReturn', 'ElectionStatusAccruedInterestDueThisReturn',
  'ElectionStatusDeferredTaxAfterPartialTerm', 'ElectionStatusIntAccruedAfterPartialTerm'
];

export const SEC926_SCALAR_AMOUNT_KEYS = [
  'transferredlossamount', 'totalanticipatedincome', 'transferinterestbefore', 'transferinterestafter'
];

export const SEC926_ARRAY_AMOUNT_KEYS = [
  'CostOtherBasis', 'IncomeInclusionOfTransfer', 'FMVOnDateOfTransfer', 'CostBasis', 'GainTransfer',
  'ArmsOnDateOfTransfer'
];
